import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCreditCard } from "react-icons/fa";
import { logout } from "../../../../utils/api/auth.js";
import axiosInstance from "../../../../utils/api/axiosInstance.js";
import GlobalHeader from "../../../../components/GlobalHeader.jsx";
import "../css/Receipt.css";
import ReceiptCard from "../components/ReceiptCard.jsx";

const getUserIdFromToken = (token) => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userId || payload.user_id;
  } catch (error) {
    console.error("Error extracting userId from token:", error);
    return null;
  }
};

const Receipt = () => {
  const nav = useNavigate();
  const location = useLocation();
  const receiptRef = useRef(null);

  const [isDownloading, setIsDownloading] = useState({});
  const [isPaid, setIsPaid] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccessMessage, setPaymentSuccessMessage] = useState("");

  const [purchaseData, setPurchaseData] = useState({
    event: {},
    packages: [],
    package: {},
    amount: 0,
    delegateDetails: {},
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    serviceFee: 0,
    total: 0,
  });

  useEffect(() => {
    const stateData = location.state?.purchaseData || location.state;

    console.log('[Receipt] 📄 State data received from route:', {
      hasEvent: !!(stateData?.event),
      packageCount: stateData?.packages?.length,
      amount: stateData?.amount,
      purchase_id: stateData?.purchase_id,
    });
    if (stateData && (stateData.event || stateData.packages)) {
      let extractedAmount = 0;
      let selectedPackage = {};

      if (stateData.packages && stateData.packages.length > 0) {
        const packageWithAmount = stateData.packages.find((pkg) => pkg.amount);
        if (packageWithAmount) {
          selectedPackage = packageWithAmount;
          const amountStr = packageWithAmount.amount.toString();
          extractedAmount = Number.parseFloat(amountStr.replace(/[R\s,]/g, "")) || 0;
        }
      }

      if (extractedAmount === 0 && stateData.amount) {
        const amountStr = stateData.amount.toString();
        extractedAmount = Number.parseFloat(amountStr.replace(/[R\s,]/g, "")) || 0;
      }

      const processedData = {
        event: stateData.event || {},
        packages: stateData.packages || [],
        package: selectedPackage,
        amount: extractedAmount,
        delegateDetails: stateData.delegateDetails || {},
        purchase_id: stateData.purchase_id,
      };

      setPurchaseData(processedData);
    }
  }, [location.state]);

  useEffect(() => {
    let subtotal = 0;

    if (purchaseData.packages && purchaseData.packages.length > 0) {
      purchaseData.packages.forEach((pkg) => {
        if (pkg.amount) {
          const amountStr = pkg.amount.toString();
          const packageAmount = Number.parseFloat(amountStr.replace(/[R\s,]/g, "")) || 0;
          subtotal += packageAmount;
        }
      });
    } else if (purchaseData.amount > 0) {
      subtotal = purchaseData.amount;
    }

    if (subtotal > 0) {
      // Use the totalAmount from route state (the real amount charged to Paystack).
      // serviceFee = totalAmount - subtotal (reflects the actual 3% + R1 fee charged).
      const stateData = location.state?.purchaseData || location.state;
      const paidTotal = stateData?.totalAmount
        ? Math.round(stateData.totalAmount * 100) / 100
        : Math.round((subtotal * 1.03 + 1) * 100) / 100;
      const serviceFee = Math.round((paidTotal - subtotal) * 100) / 100;

      console.log('[Receipt] 💰 Totals computed:', {
        subtotal,
        serviceFeeFormula: 'totalAmount − subtotal = ' + serviceFee,
        total: paidTotal,
        note: 'totalAmount comes from route state — the real amount charged by Paystack',
      });
      setTotals({
        subtotal,
        serviceFee,
        total: paidTotal,
      });
    }
  }, [purchaseData.packages, purchaseData.amount, location.state]);

  const handlePayNow = async () => {
    const eventId = purchaseData?.event?.event_id || purchaseData?.event?.id;

    if (!purchaseData || !eventId) {
      alert("No event information available");
      return;
    }

    if (isProcessingPayment || isPaid) {
      return;
    }

    try {
      setIsProcessingPayment(true);

      const token = localStorage.getItem("token");
      const userId = getUserIdFromToken(token);

      if (!token || !userId) {
        alert("Authentication required. Please log in again.");
        nav("/");
        return;
      }

      let purchaseId = purchaseData?.purchase_id;
      let databaseTicketNumber = null;

      if (!purchaseId) {
        let totalAmount = 0;
        let selectedPackage = "Standard";

        if (purchaseData.packages && purchaseData.packages.length > 0) {
          const packageWithAmount = purchaseData.packages.find((pkg) => pkg.amount);
          if (packageWithAmount) {
            selectedPackage =
              packageWithAmount.name || packageWithAmount.package || "Standard";
            const amountStr = packageWithAmount.amount.toString();
            totalAmount =
              Number.parseFloat(amountStr.replace(/[R\s,]/g, "")) || 0;
          }
        }

        if (totalAmount === 0 && purchaseData.amount) {
          const amountStr = purchaseData.amount.toString();
          totalAmount =
            Number.parseFloat(amountStr.replace(/[R\s,]/g, "")) || 0;
        }

        if (totalAmount <= 0) {
          alert("Invalid payment amount. Please check your package selection.");
          return;
        }

        const delegateDetails = {
          name: purchaseData.delegateDetails?.name || "",
          email: purchaseData.delegateDetails?.email || "",
          phone: purchaseData.delegateDetails?.phone || "",
          title: purchaseData.delegateDetails?.title || "",
          gender: purchaseData.delegateDetails?.gender || "",
          dayPass: purchaseData.delegateDetails?.dayPass || "",
          delegation: purchaseData.delegateDetails?.delegation || "",
          ieeeNumber: purchaseData.delegateDetails?.ieeeNumber || "",
        };

        console.log('[Receipt] 📤 Creating purchase — API: POST /customer/ticket-purchases', { eventId, userId, amount: totalAmount });
        const purchaseResponse = await axiosInstance.post(
          "/customer/ticket-purchases",
          {
            event_id: eventId,
            user_id: userId,
            number_of_tickets: 1,
            package: selectedPackage,
            amount: totalAmount,
            delegate_details: delegateDetails,
          }
        );

        const purchaseResult = purchaseResponse.data;
        purchaseId = purchaseResult.purchase_id;
        databaseTicketNumber = purchaseResult.ticket_number;
      } else {
        databaseTicketNumber = purchaseData?.ticket_number;
      }

      const statusUpdateData = {
        purchase_id: purchaseId,
        status: "approved",
      };
      console.log('[Receipt] 📤 Updating ticket status — API: POST /customer/update-ticket-status', { purchase_id: purchaseId });
      const statusResponse = await axiosInstance.post(
        "/customer/update-ticket-status",
        statusUpdateData
      );

      const statusResult = statusResponse.data;

      setIsPaid(true);
      setPaymentSuccessMessage("Payment processed successfully!");

      const finalTicketNumber =
        databaseTicketNumber ||
        statusResult?.data?.ticket_number ||
        statusResult?.ticket_number;

      setPurchaseData((prev) => {
        const updatedPurchaseData = {
          ...prev,
          purchase_id: purchaseId,
          ticket_number: finalTicketNumber,
        };

        if (updatedPurchaseData.packages && updatedPurchaseData.packages.length > 0) {
          updatedPurchaseData.packages = updatedPurchaseData.packages.map((pkg) => ({
            ...pkg,
            ticket_number: finalTicketNumber,
            ticketNumber: finalTicketNumber,
          }));
        }

        return updatedPurchaseData;
      });
    } catch (error) {
      console.error("Payment error:", error);
      alert(`Payment failed: ${error.message}`);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  useEffect(() => {
    const stateData = location.state?.purchaseData || location.state;

    if (!stateData || (!stateData.event && !stateData.packages)) {
      setTimeout(() => {
        nav("/customerdash");
      }, 1000);
    }
  }, [location, nav]);

  const getDelegateInfo = (pkg) => {
    if (pkg.name || pkg.email || pkg.phone) {
      return {
        name: pkg.name || "",
        surname: "",
        email: pkg.email || "",
        phone: pkg.phone || "",
      };
    }

    if (pkg.delegate_details && typeof pkg.delegate_details === "string") {
      try {
        const delegateDetails = JSON.parse(pkg.delegate_details);
        return {
          name: delegateDetails.name || "",
          surname: "",
          email: delegateDetails.email || "",
          phone: delegateDetails.phone || "",
        };
      } catch (e) {
        console.error("Error parsing delegate_details:", e);
      }
    }

    if (pkg.delegate_details && typeof pkg.delegate_details === "object") {
      return {
        name: pkg.delegate_details.name || "",
        surname: "",
        email: pkg.delegate_details.email || "",
        phone: pkg.delegate_details.phone || "",
      };
    }

    return {
      name: "",
      surname: "",
      email: "",
      phone: "",
    };
  };

  const generateFallbackTicketNumber = (id) => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(1000 + Math.random() * 9000);
    return `${timestamp}${random}${id || ""}`.replace(/\D/g, "").slice(0, 10);
  };

  const handleViewTicket = async (pkg, index) => {
    if (!purchaseData || !purchaseData.event) {
      alert("No ticket information available");
      return;
    }

    setIsDownloading((prev) => ({ ...prev, [index]: true }));

    try {
      const delegateInfo = getDelegateInfo(pkg);

      let ticketNumber = pkg.ticket_number || pkg.ticketNumber;

      if (!ticketNumber) {
        ticketNumber = purchaseData.ticket_number;
      }

      if (!ticketNumber) {
        ticketNumber = generateFallbackTicketNumber(pkg.ticketId || pkg.id);
      }

      const ticketData = {
        id: pkg.ticketId || `TKT-${ticketNumber}`,
        ticket_number: ticketNumber,
        ticketNumber: ticketNumber,
        ticketType: pkg.packageDetails || "Standard Ticket",
        price: Number.parseFloat(pkg.amount?.replace(/[^0-9.]/g, "")) || 0,
        status: "Approved",
        delegate: {
          name: delegateInfo.name || "",
          surname: delegateInfo.surname || "",
          email: delegateInfo.email || "",
          phone: delegateInfo.phone,
        },
        eventName: purchaseData.event?.name || "Event",
        eventDate:
          purchaseData.event?.startdate ||
          purchaseData.event?.start_date ||
          purchaseData.event?.date,
        eventLocation: purchaseData.event?.location || "TBA",
        eventImage: purchaseData.event?.eventImage || "",
        quantity: pkg.tickets || 1,
        barcode: ticketNumber,
        qr_code: ticketNumber,
      };

      const eventInfo = {
        ...purchaseData.event,
        date:
          purchaseData.event?.startdate ||
          purchaseData.event?.start_date ||
          purchaseData.event?.date,
        time:
          purchaseData.event?.formattedTime ||
          purchaseData.event?.time ||
          purchaseData.event?.start_time,
        location: purchaseData.event?.location || "TBA",
      };

      nav("/viewtickets", {
        state: {
          ticket: ticketData,
          eventInfo: eventInfo,
        },
      });
    } catch (error) {
      console.error("Error preparing ticket data:", error);
      alert("Error loading ticket. Please try again.");
    } finally {
      setIsDownloading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const receiptDate = new Date().toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const receiptTime = new Date().toLocaleTimeString("en-ZA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const getAmPm = (date) => {
    const hours = date.getHours();
    return hours >= 12 ? "PM" : "AM";
  };

  const receiptTimeWithAmPm = `${receiptTime} ${getAmPm(new Date())}`;

  const formatEventDate = (rawDate) => {
    if (!rawDate) return "TBA";
    try {
      const parsed = new Date(rawDate);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString("en-ZA", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
    } catch (error) {
      console.error("Error formatting date:", error);
    }
    return rawDate;
  };

  const formatEventTime = (rawTime) => {
    if (!rawTime) return "TBA";
    try {
      const timeParts = rawTime.split(':');
      if (timeParts.length >= 2) {
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
          const date = new Date();
          date.setHours(hours, minutes, 0, 0);
          
          const time24 = date.toLocaleTimeString("en-ZA", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
          
          const ampm = hours >= 12 ? "PM" : "AM";
          
          return `${time24} ${ampm}`;
        }
      }
    } catch (error) {
      console.error("Error formatting time:", error);
    }
    return rawTime;
  };

  const receiptNumber = `XPT-${Date.now().toString().slice(-8)}`;

  return (
    <div className="payment-page" ref={receiptRef}>
      <GlobalHeader
        title="Receipt"
        subtitle="Your purchase confirmation and ticket details"
        backTo="/customerdash"
        logoutTo="/"
        onLogout={() => logout(nav)}
      />

      <main className="payment-main">
        <ReceiptCard
          purchaseData={purchaseData}
          totals={totals}
          receiptNumber={receiptNumber}
          receiptDate={receiptDate}
          receiptTime={receiptTimeWithAmPm}
          formatEventDate={formatEventDate}
          formatEventTime={formatEventTime}
          paymentSuccessMessage={paymentSuccessMessage}
          isDownloading={isDownloading}
          onViewTicket={handleViewTicket}
        />
      </main>
    </div>
  );
};

export default Receipt;
